import { UserRole } from "constants/userRole";
import { useMemo } from "react";
import { useGetAccountByIdQuery } from "store/queries/account";

import { useCurrentUser } from "./useCurrentUser";

/**
 * CLE-2811 — whether the Contract Management chat widget should be shown for an account.
 *
 * Two conditions, both required:
 *  1. the account has a Contract Management bot assigned (the "Contract Management Chat Bot Enabled"
 *     feature flag on the hospital's edit page), and
 *  2. the viewer is a super admin.
 *
 * The role check is not decoration: every staff chat route on the API carries
 * `validateSuperAdminMiddleware`, so showing the widget to anyone else produces a widget that 403s on
 * its first message. Opening this to hospital staff with contracts-library access means relaxing both
 * sides together, which is its own piece of work.
 *
 * Extracted from FeeScheduleComparison so the Library tab and the contract page gate identically —
 * three copies of this condition is how one of them ends up wrong.
 */
export function useContractManagementBot(accountId?: string): boolean {
  const { data: accountResponse } = useGetAccountByIdQuery(accountId!, {
    skip: !accountId,
  });
  const { userInfo } = useCurrentUser({ skip: false });

  return useMemo(
    () =>
      !!accountResponse?.data?.contractManagementChatBotId &&
      userInfo?.scope === UserRole.SUPER_ADMIN,
    [accountResponse?.data?.contractManagementChatBotId, userInfo?.scope]
  );
}

export default useContractManagementBot;
